// NumberField — canonical `number` field primitive (comp-ε GAP-3b-i,
// CHG-2433). Numeric input. Storage: number (not string) — the onChange
// emits a parsed number, or null when cleared / non-numeric. For money use
// `amount` (AmountField); for currency-agnostic counts/limits use this.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('number') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the input via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the input; aria-describedby links the input to its error + helper text
// nodes; inputMode="numeric" for touch keypads; native <input type=number> focus.

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface NumberFieldProps extends FieldPrimitiveProps<number> {
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

export function NumberField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  placeholder,
  min,
  max,
  step,
}: NumberFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <input
        id={ids.inputId}
        name={name}
        type="number"
        inputMode="numeric"
        value={value ?? ""}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const parsed = Number(raw);
          onChange(Number.isNaN(parsed) ? null : parsed);
        }}
        style={fieldControlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}
